import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as Yup from "yup";
import {deleteIn, getIn, setIn} from "../../utils/LodashUtils";
import {get, isArray, isEqual, setWith} from "lodash";
import useUpdateOnlyEffect from "../effect/useUpdateOnlyEffect";

export const useForm = (onFormSubmitCallback, initialValues = {}, validation = undefined, onValidationFail = () => {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [validationSchema, setValidationSchema] = useState(validation ? Yup.object().shape(validation) : undefined);
  const [initializedVlidationSchema, setInitializedValidationSchema] = useState(validation ? Yup.object().shape(validation) : undefined);
  const isFormEdited = useRef(false);
  const [isFormValid, setIsFormValid] = useState(true);

  const validationTrigger = useRef([]);

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (validationSchema) {
      triggerValidation(undefined, onFormSubmitCallback);
    } else {
      onFormSubmitCallback(values);
    }
  }

  const setValue = (name, value, triggerValidationKey = undefined, setIsFormEdited = false) => {
    if (triggerValidationKey) {
      triggerValidationKey = isArray(triggerValidationKey) ? triggerValidationKey : [triggerValidationKey]
      validationTrigger.current = [...validationTrigger.current, ...triggerValidationKey];
    }
    setValues(previousValues => setIn(previousValues, name, value));
    if(setIsFormEdited) {
      isFormEdited.current = true;
    }
  }

  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setIsFormValid(true);
  }

  const setFormNotDirty = (clearForm = false) => {
    isFormEdited.current = false;
    if (clearForm) {
      setValues(initialValues);
    }
  }

  const triggerValidation = (names, callback) => {
    if (!validationSchema) return;

    let validationErrors = {};

    validationSchema.validate(values, {abortEarly: false, context: {data: values}}).catch(validationError => {
      validationErrors = validationError.inner.reduce((validationErrors, item) => {
        validationErrors = setWith(validationErrors, item.path, item.message);
        return validationErrors;
      }, {});
    }).then(() => {
      //if we validate whole scheme just override errors with validation errors
      let newErrors = validationErrors;
      if (names) {
        names = isArray(names) ? names : [names];
        newErrors = {...errors};

        //delete errors we triggered validation for
        names.forEach(name => {
          newErrors = deleteIn(newErrors, name, true);
        });

        //set errors for fields we triggered validation for if exists
        names.forEach(name => {
          const fieldError = getIn(validationErrors, name);
          if (fieldError) {
            newErrors = setIn(newErrors, name, fieldError);
          }
        });
      }

      setErrors(newErrors);
      setIsFormValid(Object.keys(newErrors).length === 0);
      if(callback != null && onValidationFail != null && Object.keys(validationErrors).length !== 0) {
        onValidationFail()
        return;
      }
      callback && Object.keys(validationErrors).length === 0 && callback(values);

    });

  }
  const removeField = (name) => {
    setValues(prevValues => deleteIn(prevValues, name, true));
  };
  const getField = (name) => {
    return {
      value: getIn(values, name),
      error: getIn(errors, name),
      setValue: (value, triggerValidationKey, setIsFormEdited) =>
          setValue(name, value, triggerValidationKey, setIsFormEdited),
      triggerValidation: () => triggerValidation(name),
      clearValidation: () => clearValidation(name),
      getField: (nestedName) => getField(`${name}.${nestedName}`),
      removeField: (nestedName) => removeField(`${name}.${nestedName}`)
    }
  };
  // const getField = (name) => {
  //   return {
  //     value: getIn(values, name),
  //     error: getIn(errors, name),
  //     setValue: (value, triggerValidationKey, setIsFormEdited) => setValue(name, value, triggerValidationKey, setIsFormEdited),
  //     triggerValidation: () => triggerValidation(name),
  //     clearValidation: () => clearValidation(name)
  //   }
  // }
  //   const getField = (name) => useCallback((name) => {
  //           return {
  //               value: getIn(values, name),
  //               error: getIn(errors, name),
  //               setValue: (value, triggerValidationKey, setIsFormEdited) => setValue(name, value, triggerValidationKey, setIsFormEdited),
  //               triggerValidation: () => triggerValidation(name),
  //               clearValidation: () => clearValidation(name)
  //           }
  //       }
  //       , [getIn(values, name)]
  //   )

  const watch = (selector) => {
    if (Array.isArray(selector)) {
      return selector.map(selector => get(values, selector, undefined));
    }
    return get(values, selector, undefined);
  }

  const clearValidation = (fields = undefined) => {
    let errorObj = {};

    if (fields) {
      if (!isArray(fields)) {
        fields = [fields]
      }
      errorObj = Object.entries(errors).reduce((errorList, [key, value]) => {
        if (!fields.includes(key)) {
          errorList[key] = value;
        }
        return errorList;
      }, {});
    }
    setErrors(errorObj);
  }

  const isEdited = () =>  {
    return isFormEdited.current
  }

  useEffect(() => {
    if (validationTrigger.current.length) {
      triggerValidation([...validationTrigger.current]);
      validationTrigger.current = [];
    }
  }, [values]);

  useUpdateOnlyEffect(() => {
    setValidationSchema(initializedVlidationSchema);
    if (validationTrigger.current.length) {
      triggerValidation([...Object.keys(errors), ...validationTrigger.current]);
      validationTrigger.current = [];
    } else {
      triggerValidation([...Object.keys(errors)]);
    }
  }, [initializedVlidationSchema]);

  const reinitSchema = (schema) => {
    setInitializedValidationSchema(schema ? Yup.object().shape(schema) : validationSchema);
  }

  return {
    handleSubmit,
    values,
    setValue,
    setValues,
    setFormNotDirty,
    clearValidation,
    triggerValidation,
    errors,
    setErrors,
    validationSchema,
    setValidationSchema,
    getField,
    watch,
    isEdited,
    isFormValid,
    reinitSchema
  };
}
